import { useState } from "react";
import { Upload, Camera, CheckCircle2, Loader2, X, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";

const CHECKLIST = [
  "A 3/4 view that includes the rego plate, side and rear",
  "Any photos of the damage",
  "The compliance / VIN plate",
  "A full view of the impacted area (e.g. the full side or roof)",
  "A close-up of the sealing in that area, if the damage relates to water ingress",
];

const FIELDS = [
  { k: "name", label: "Your name", required: true },
  { k: "phone", label: "Phone", required: true },
  { k: "email", label: "Email", type: "email", required: true },
  { k: "insurer", label: "Insurer (NRMA, RACQ, etc.)", required: true },
  { k: "claim_number", label: "Claim / reference number (if you have one)" },
  { k: "rego", label: "Caravan rego" },
  { k: "caravan_make_model_year", label: "Make / model / year" },
  { k: "van_location", label: "Where is the van right now?" },
];

function Field({ label, type = "text", required, value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-[#16A34A]"
      />
    </div>
  );
}

export default function Insurance() {
  const [form, setForm] = useState({
    name:"", email:"", phone:"", insurer:"", claim_number:"",
    caravan_make_model_year:"", rego:"", van_location:"", description:""
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const addFiles = (incoming) => {
    const arr = Array.from(incoming);
    const combined = [...files, ...arr].slice(0, 12);
    setFiles(combined);
  };

  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.insurer || !form.description) {
      toast.error("Please fill in the required fields"); return;
    }
    if (files.length === 0) {
      toast.error("Please attach at least one photo"); return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const r = await fetch(`${API}/claims`, { method: "POST", body: fd });
      if (!r.ok) throw new Error(await r.text());
      setDone(true);
      toast.success("Sent — Nomad will be in touch shortly");
    } catch (err) {
      toast.error("Upload failed. Try fewer / smaller files.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="bg-white min-h-screen">
        <div className="container mx-auto px-6 py-24 max-w-2xl text-center">
          <CheckCircle2 className="w-16 h-16 text-[#16A34A] mx-auto" />
          <h1 className="font-display text-4xl font-black tracking-tighter mt-6">Thanks — we've got it.</h1>
          <p className="mt-4 text-zinc-600">Nomad will review your photos and prepare the quote for your insurer. Someone will be in contact within 1–2 business days.</p>
          <div data-testid="insurance-followup-note" className="mt-8 inline-block border border-[#16A34A]/40 bg-[#16A34A]/10 px-5 py-3 text-sm text-zinc-800 max-w-md">
            Need to add more photos or details later? Just submit this same form again — we'll automatically link it to your existing claim, and someone will reply to you shortly.
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-zinc-50 min-h-screen">
      <section className="bg-zinc-950 text-white">
        <div className="container mx-auto px-6 py-16 max-w-3xl">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#22C55E] mb-4">◆ Insurance Quote · Photo Intake</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Send us your photos.</h1>
          <p className="mt-4 text-zinc-300 max-w-xl">Your insurer has asked Nomad to quote. Skip the drive — submit photos of the damage here and we'll prepare your quote remotely.</p>
        </div>
      </section>

      <section className="container mx-auto px-6 py-12 max-w-3xl">
        <div data-testid="insurer-notify-notice" className="bg-[#16A34A]/10 border border-[#16A34A]/40 p-6 mb-8 flex gap-3">
          <Info className="w-5 h-5 text-[#15803D] shrink-0 mt-0.5" />
          <div className="text-sm text-zinc-800 leading-relaxed">
            <div className="font-display font-bold text-zinc-950 mb-1">Before we can proceed — notify your insurer</div>
            If you haven't already, please contact your insurer and let them know that <span className="font-semibold">Nomad Caravan Repairs Whitsundays</span> is your preferred repairer. Your insurer will then send us all the paperwork required to proceed with your claim.
          </div>
        </div>

        <div className="bg-white border border-zinc-200 p-6 mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#16A34A] mb-3 flex items-center gap-2"><Camera className="w-3.5 h-3.5" /> Photo Checklist</div>
          <ul className="space-y-2 text-sm text-zinc-700">
            {CHECKLIST.map((c) => <li key={c} className="flex gap-2"><span className="text-[#16A34A]">✓</span>{c}</li>)}
          </ul>
          <div data-testid="photo-safety-disclaimer" className="mt-4 border-t border-zinc-200 pt-4 flex gap-2 text-xs text-zinc-600 leading-relaxed">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <span>Safety first: only climb a ladder or take photos of what you're willing and able to do safely. We don't encourage anything unsafe — if you can't reach or capture something, just leave it out and we'll sort it when we see the van.</span>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5 bg-white border border-zinc-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map(({ k, label, type, required }) => (
              <Field
                key={k}
                label={label}
                type={type}
                required={required}
                value={form[k]}
                onChange={(e) => setForm((prev) => ({ ...prev, [k]: e.target.value }))}
                testid={`claim-${k}`}
              />
            ))}
          </div>
          <p className="text-xs text-zinc-500 -mt-1">No claim number yet? No problem — leave it blank. If you haven't lodged with your insurer yet, just submit anyway and we'll add the reference once it comes through.</p>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Description of damage *</label>
            <textarea
              rows={4}
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              required
              data-testid="claim-description"
              className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-[#16A34A]"
            />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Photos & videos ({files.length}/12)</label>
            <label className="mt-2 border-2 border-dashed border-zinc-300 hover:border-[#16A34A] transition-colors p-8 flex flex-col items-center justify-center cursor-pointer">
              <Upload className="w-8 h-8 text-zinc-400 mb-3" />
              <span className="text-sm font-semibold text-zinc-700">Tap to add photos or a short video</span>
              <span className="text-xs text-zinc-500 mt-1">JPG · PNG · MP4 · up to 25MB each</span>
              <input
                type="file"
                multiple
                accept="image/*,video/mp4,video/quicktime,video/webm"
                onChange={(e) => addFiles(e.target.files)}
                data-testid="claim-files"
                className="hidden"
              />
            </label>
            {files.length > 0 && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {files.map((f, i) => (
                  <div key={i} className="border border-zinc-200 p-2 flex items-center gap-2 text-xs">
                    <span className="truncate flex-1">{f.name}</span>
                    <button type="button" onClick={() => removeFile(i)} data-testid={`claim-file-remove-${i}`}><X className="w-3.5 h-3.5 text-zinc-500" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="claim-submit"
            className="w-full bg-[#16A34A] text-white px-6 py-4 font-semibold hover:bg-[#15803D] disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <>Submit to Nomad</>}
          </button>
        </form>
      </section>
    </main>
  );
}
